import React, { useState, useEffect } from "react";
import {
  Database,
  Plus,
  Edit2,
  Trash2,
  Search,
  Filter,
  X,
  Loader2,
  RefreshCw,
  AlertTriangle,
  Shield,
  MapPin,
  User,
  Calendar,
  Clock,
  Lock,
  Eye,
  CheckCircle,
  XCircle,
  FileText,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { supabase } from "../lib/supabase";

// ============================================
// FONCTIONS SUPABASE
// ============================================

const loadInventaireFromDB = async (entrepriseId) => {
  const { data, error } = await supabase
    .from("inventaire_donnees")
    .select("*")
    .eq("entreprise_id", entrepriseId)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return data || [];
};

const addInventaireToDB = async (itemData) => {
  const { data, error } = await supabase
    .from("inventaire_donnees")
    .insert([itemData])
    .select()
    .single();

  if (error) throw error;
  return data;
};

const updateInventaireInDB = async (id, updates) => {
  const { data, error } = await supabase
    .from("inventaire_donnees")
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;
  return data;
};

const deleteInventaireFromDB = async (id) => {
  const { error } = await supabase
    .from("inventaire_donnees")
    .delete()
    .eq("id", id);

  if (error) throw error;
  return true;
};

// ============================================
// COMPOSANT PRINCIPAL
// ============================================

const InventaireManager = ({ showToast, entrepriseId }) => {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterSensibilite, setFilterSensibilite] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [expandedItem, setExpandedItem] = useState(null);

  const initialFormData = {
    nom: "",
    type: "Client",
    description: "",
    donnees_contenues: "",
    sensibilite: "Standard",
    localisation: "",
    responsable: "",
    retention: "",
    justification_retention: "",
    // Conformité Loi 25
    finalites: "", // Texte décrivant les fins de la collecte
    base_legale: "Consentement", // Base légale du traitement
    consentement_explicite: false,
    communication_hors_quebec: false, // Si données transférées hors QC
    pays_destinataire: "",
    minimisation_validee: false,
    acces_limite: false,
    securite: [],
    date_derniere_revision: "",
    prochaine_revision: "",
  };

  const [formData, setFormData] = useState(initialFormData);

  // ============================================
  // SYSTÈME DE CONFORMITÉ LOI 25 (RÉEL)
  // ============================================

  const evaluateConformity = () => {
    try {
      const checks = {
        // OBLIGATIONS LÉGALES (Articles 3.1, 3.2, 8, 12, 13 de la Loi 25)
        legal: [
          {
            key: "nom",
            label: "Identification des données",
            article: "Art. 4",
            passed:
              !!formData.nom?.trim() && !!formData.donnees_contenues?.trim(),
            required: true,
          },
          {
            key: "finalites",
            label: "Finalités déterminées",
            article: "Art. 4, 5",
            passed: !!formData.finalites?.trim(),
            required: true,
          },
          {
            key: "responsable",
            label: "Responsable désigné",
            article: "Art. 3.1",
            passed: !!formData.responsable?.trim(),
            required: true,
          },
          {
            key: "retention",
            label: "Durée de conservation définie",
            article: "Art. 12",
            passed: !!formData.retention?.trim(),
            required: true,
          },
          {
            key: "base_legale",
            label: "Base légale établie",
            article: "Art. 14",
            passed: !!formData.base_legale,
            required: true,
          },
        ],
        // SÉCURITÉ ET PROTECTION (Article 10)
        security: [
          {
            key: "localisation",
            label: "Lieu de conservation identifié",
            article: "Art. 17",
            passed: !!formData.localisation?.trim(),
            required: true,
          },
          {
            key: "securite",
            label: "Mesures de sécurité en place",
            article: "Art. 10",
            passed:
              Array.isArray(formData.securite) && formData.securite.length > 0,
            required: true,
          },
          {
            key: "acces",
            label: "Accès limité aux personnes autorisées",
            article: "Art. 10",
            passed: !!formData.acces_limite,
            required: false,
          },
        ],
        // TRANSFERT HORS QUÉBEC (Article 17)
        transfer: [
          {
            key: "hors_qc",
            label: "Transfert hors Québec documenté",
            article: "Art. 17",
            passed:
              !formData.communication_hors_quebec ||
              (formData.communication_hors_quebec &&
                !!formData.pays_destinataire?.trim()),
            required: !!formData.communication_hors_quebec,
            conditional: true,
          },
        ],
        // PRINCIPES DE PROTECTION (Articles 4, 5, 6)
        principles: [
          {
            key: "minimisation",
            label: "Minimisation des données",
            article: "Art. 5",
            passed: !!formData.minimisation_validee,
            required: false,
          },
          {
            key: "sensibilite",
            label: "Sensibilité évaluée",
            article: "Art. 12, 13",
            passed:
              !!formData.sensibilite && formData.sensibilite !== "Standard",
            required: false,
          },
        ],
      };

      // Calcul des scores
      const legalChecks = checks.legal.filter((c) => c.required);
      const legalPassed = legalChecks.filter((c) => c.passed).length;

      const securityChecks = checks.security.filter((c) => c.required);
      const securityPassed = securityChecks.filter((c) => c.passed).length;

      const transferOk = checks.transfer.every((c) => c.passed);

      const principlesPassed = checks.principles.filter((c) => c.passed).length;

      const totalRequired = legalChecks.length + securityChecks.length;
      const totalRequiredPassed = legalPassed + securityPassed;
      const percentage =
        totalRequired > 0
          ? Math.round((totalRequiredPassed / totalRequired) * 100)
          : 0;

      // Déterminer le statut selon la Loi 25
      let status, statusColor, statusBg, statusIcon, statusDescription;

      if (totalRequiredPassed < totalRequired) {
        status = "Non conforme";
        statusColor = "text-red-600 dark:text-red-400";
        statusBg = "bg-red-50 dark:bg-red-900/20";
        statusIcon = "🔴";
        statusDescription = "Obligations légales non respectées";
      } else if (!transferOk) {
        status = "Non conforme";
        statusColor = "text-red-600 dark:text-red-400";
        statusBg = "bg-red-50 dark:bg-red-900/20";
        statusIcon = "🔴";
        statusDescription = "Transfert hors Québec non documenté";
      } else if (principlesPassed < 2) {
        status = "À réviser";
        statusColor = "text-amber-600 dark:text-amber-400";
        statusBg = "bg-amber-50 dark:bg-amber-900/20";
        statusIcon = "🟡";
        statusDescription = "Conforme mais améliorations recommandées";
      } else {
        status = "Conforme";
        statusColor = "text-green-600 dark:text-green-400";
        statusBg = "bg-green-50 dark:bg-green-900/20";
        statusIcon = "🟢";
        statusDescription = "Toutes les exigences Loi 25 respectées";
      }

      return {
        checks,
        legalPassed,
        legalTotal: legalChecks.length,
        securityPassed,
        securityTotal: securityChecks.length,
        principlesPassed,
        principlesTotal: checks.principles.length,
        transferOk,
        percentage,
        status,
        statusColor,
        statusBg,
        statusIcon,
        statusDescription,
      };
    } catch (err) {
      console.error("Error in evaluateConformity:", err);
      return {
        checks: { legal: [], security: [], transfer: [], principles: [] },
        legalPassed: 0,
        legalTotal: 5,
        securityPassed: 0,
        securityTotal: 2,
        principlesPassed: 0,
        principlesTotal: 2,
        transferOk: true,
        percentage: 0,
        status: "Non conforme",
        statusColor: "text-red-600 dark:text-red-400",
        statusBg: "bg-red-50 dark:bg-red-900/20",
        statusIcon: "🔴",
        statusDescription: "Erreur d'évaluation",
      };
    }
  };

  const conformity = evaluateConformity();

  // Fonction pour évaluer la conformité d'un item existant (avec protection contre champs manquants)
  const getItemConformityStatus = (item) => {
    if (!item)
      return { status: "Non conforme", color: "bg-red-500", icon: "🔴" };

    try {
      // Obligations légales (avec fallback pour anciens champs)
      const hasIdentification =
        !!item.nom?.trim() &&
        !!(item.donnees_contenues?.trim() || item.description?.trim());
      const hasFinalites = !!(item.finalites?.trim() || item.finalite_definie);
      const hasResponsable = !!item.responsable?.trim();
      const hasRetention = !!item.retention?.trim();
      const hasBaseLegale = !!(item.base_legale || item.consentement);

      // Sécurité
      const hasLocalisation = !!item.localisation?.trim();
      const hasSecurite =
        Array.isArray(item.securite) && item.securite.length > 0;

      // Transfert (optionnel - seulement si hors Québec)
      const transferOk =
        !item.communication_hors_quebec ||
        (item.communication_hors_quebec && !!item.pays_destinataire?.trim());

      const legalOk =
        hasIdentification &&
        hasFinalites &&
        hasResponsable &&
        hasRetention &&
        hasBaseLegale;
      const securityOk = hasLocalisation && hasSecurite;

      if (!legalOk || !securityOk || !transferOk) {
        return { status: "Non conforme", color: "bg-red-500", icon: "🔴" };
      } else if (
        !(item.minimisation_validee || item.minimisation) ||
        !item.acces_limite
      ) {
        return { status: "À réviser", color: "bg-amber-500", icon: "🟡" };
      } else {
        return { status: "Conforme", color: "bg-green-500", icon: "🟢" };
      }
    } catch (err) {
      console.warn("Error evaluating conformity:", err);
      return { status: "Non conforme", color: "bg-red-500", icon: "🔴" };
    }
  };

  const basesLegales = [
    "Consentement",
    "Exécution d'un contrat",
    "Obligation légale",
    "Intérêt légitime",
    "Intérêt public",
  ];

  const types = [
    "Client",
    "Employé",
    "Fournisseur",
    "Partenaire",
    "Prospect",
    "Autre",
  ];
  const sensibilites = [
    "Standard",
    "Confidentiel",
    "Très confidentiel",
    "Critique",
  ];
  const securiteOptions = [
    "Chiffrement",
    "Accès restreint",
    "Authentification MFA",
    "Sauvegarde",
    "Journalisation",
    "Pare-feu",
  ];

  const sensibiliteColors = {
    Standard:
      "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
    Confidentiel:
      "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
    "Très confidentiel":
      "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400",
    Critique: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  };

  // Charger les données
  useEffect(() => {
    if (entrepriseId) {
      loadData();
    } else {
      setIsLoading(false);
    }
  }, [entrepriseId]);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await loadInventaireFromDB(entrepriseId);
      setItems(data);
    } catch (err) {
      setError("Erreur lors du chargement de l'inventaire.");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  // Filtrage
  const filteredItems = items.filter((item) => {
    const matchesSearch =
      (item.nom || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.description || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (item.responsable || "").toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSensibilite =
      filterSensibilite === "all" || item.sensibilite === filterSensibilite;
    const matchesType = filterType === "all" || item.type === filterType;
    return matchesSearch && matchesSensibilite && matchesType;
  });

  // Stats (avec protection contre erreurs)
  const stats = {
    total: items.length,
    conforme: items.filter((i) => {
      try {
        return getItemConformityStatus(i).status === "Conforme";
      } catch {
        return false;
      }
    }).length,
    aReviser: items.filter((i) => {
      try {
        return getItemConformityStatus(i).status === "À réviser";
      } catch {
        return false;
      }
    }).length,
    nonConforme: items.filter((i) => {
      try {
        return getItemConformityStatus(i).status === "Non conforme";
      } catch {
        return true;
      }
    }).length,
  };

  const handleAdd = () => {
    setEditingItem(null);
    setFormData(initialFormData);
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      nom: item.nom || "",
      type: item.type || "Client",
      description: item.description || "",
      donnees_contenues: item.donnees_contenues || "",
      sensibilite: item.sensibilite || "Standard",
      localisation: item.localisation || "",
      responsable: item.responsable || "",
      retention: item.retention || "",
      justification_retention: item.justification_retention || "",
      // Champs Loi 25
      finalites: item.finalites || "",
      base_legale: item.base_legale || "Consentement",
      consentement_explicite: item.consentement_explicite || false,
      communication_hors_quebec: item.communication_hors_quebec || false,
      pays_destinataire: item.pays_destinataire || "",
      minimisation_validee: item.minimisation_validee || false,
      acces_limite: item.acces_limite || false,
      securite: item.securite || [],
      date_derniere_revision: item.date_derniere_revision || "",
      prochaine_revision: item.prochaine_revision || "",
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.nom.trim()) {
      showToast?.("Le nom est obligatoire.", "error");
      return;
    }

    // Vérifier que l'entreprise est définie
    if (!entrepriseId) {
      showToast?.("Erreur: Aucune entreprise sélectionnée.", "error");
      return;
    }

    setIsSaving(true);

    try {
      // Fonction helper pour convertir "" en null (requis pour PostgreSQL DATE)
      const toNullIfEmpty = (val) =>
        val && val.trim && val.trim() !== "" ? val : null;

      // Préparer les données (enlever les champs vides)
      const dataToSave = {
        nom: formData.nom.trim(),
        type: formData.type || "Client",
        description: toNullIfEmpty(formData.description),
        donnees_contenues: toNullIfEmpty(formData.donnees_contenues),
        sensibilite: formData.sensibilite || "Standard",
        localisation: toNullIfEmpty(formData.localisation),
        responsable: toNullIfEmpty(formData.responsable),
        retention: toNullIfEmpty(formData.retention),
        justification_retention: toNullIfEmpty(
          formData.justification_retention
        ),
        // Champs Loi 25
        finalites: toNullIfEmpty(formData.finalites),
        base_legale: formData.base_legale || "Consentement",
        consentement_explicite: formData.consentement_explicite || false,
        communication_hors_quebec: formData.communication_hors_quebec || false,
        pays_destinataire: formData.communication_hors_quebec
          ? toNullIfEmpty(formData.pays_destinataire)
          : null,
        minimisation_validee: formData.minimisation_validee || false,
        acces_limite: formData.acces_limite || false,
        securite: formData.securite || [],
        // DATES: explicitement null si vide
        date_derniere_revision: toNullIfEmpty(formData.date_derniere_revision),
        prochaine_revision: toNullIfEmpty(formData.prochaine_revision),
      };

      console.log("Saving data:", dataToSave);

      if (editingItem) {
        // Modification
        const updated = await updateInventaireInDB(editingItem.id, dataToSave);
        setItems(items.map((i) => (i.id === editingItem.id ? updated : i)));
        showToast?.("Donnée modifiée avec succès!", "success");
      } else {
        // Création
        const newItem = await addInventaireToDB({
          ...dataToSave,
          entreprise_id: entrepriseId,
        });
        setItems([newItem, ...items]);
        showToast?.("Donnée ajoutée avec succès!", "success");
      }

      // Fermer le modal et réinitialiser
      setShowModal(false);
      setFormData(initialFormData);
    } catch (err) {
      console.error("Save error:", err);
      showToast?.(
        `Erreur: ${err.message || "Impossible d'enregistrer"}`,
        "error"
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteInventaireFromDB(id);
      setItems(items.filter((i) => i.id !== id));
      showToast?.("Donnée supprimée.", "success");
    } catch (err) {
      showToast?.("Erreur lors de la suppression.", "error");
      console.error(err);
    }
    setShowDeleteConfirm(null);
  };

  const toggleSecurite = (option) => {
    const current = formData.securite || [];
    if (current.includes(option)) {
      setFormData({
        ...formData,
        securite: current.filter((s) => s !== option),
      });
    } else {
      setFormData({ ...formData, securite: [...current, option] });
    }
  };

  // États de chargement
  if (!entrepriseId) {
    return (
      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-6 text-center">
        <AlertTriangle size={40} className="text-amber-500 mx-auto mb-4" />
        <p className="text-amber-700 dark:text-amber-300">
          Veuillez sélectionner une entreprise.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 size={40} className="text-sky-600 animate-spin mb-4" />
        <p className="text-gray-500 dark:text-gray-400">
          Chargement de l'inventaire...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
        <AlertTriangle size={40} className="text-red-500 mx-auto mb-4" />
        <p className="text-red-700 dark:text-red-300 font-medium mb-4">
          {error}
        </p>
        <button
          onClick={loadData}
          className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 inline-flex items-center gap-2"
        >
          <RefreshCw size={16} /> Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white">
            Inventaire des données
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Registre des renseignements personnels - Loi 25
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={loadData}
            className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            title="Actualiser"
          >
            <RefreshCw size={20} className="text-gray-600 dark:text-gray-400" />
          </button>
          <button
            onClick={handleAdd}
            className="bg-sky-600 text-white px-4 py-2 rounded-lg hover:bg-sky-700 flex items-center gap-2 shadow-lg"
          >
            <Plus size={18} />
            <span>Ajouter</span>
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-4 border-l-4 border-sky-500">
          <div className="flex items-center gap-3">
            <Database className="text-sky-500" size={24} />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {stats.total}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Total</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-4 border-l-4 border-green-500">
          <div className="flex items-center gap-3">
            <CheckCircle className="text-green-500" size={24} />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {stats.conforme}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Conforme
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-4 border-l-4 border-amber-500">
          <div className="flex items-center gap-3">
            <Clock className="text-amber-500" size={24} />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {stats.aReviser}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                À réviser
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-4 border-l-4 border-red-500">
          <div className="flex items-center gap-3">
            <AlertTriangle className="text-red-500" size={24} />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {stats.nonConforme}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Non conforme
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search
              size={20}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-sky-500"
            />
          </div>
          <select
            value={filterSensibilite}
            onChange={(e) => setFilterSensibilite(e.target.value)}
            className="px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
          >
            <option value="all">Toutes sensibilités</option>
            {sensibilites.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
          >
            <option value="all">Tous les types</option>
            {types.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Liste */}
      <div className="space-y-4">
        {filteredItems.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-12 text-center">
            <Database
              size={48}
              className="text-gray-300 dark:text-gray-600 mx-auto mb-4"
            />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              Aucune donnée trouvée
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              {items.length === 0
                ? "Commencez par ajouter vos premiers renseignements personnels."
                : "Aucun résultat ne correspond à vos filtres."}
            </p>
            {items.length === 0 && (
              <button
                onClick={handleAdd}
                className="bg-sky-600 text-white px-6 py-3 rounded-lg hover:bg-sky-700 inline-flex items-center gap-2"
              >
                <Plus size={18} /> Ajouter une donnée
              </button>
            )}
          </div>
        ) : (
          filteredItems.map((item) => (
            <div
              key={item.id}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden"
            >
              <div
                className="p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50"
                onClick={() =>
                  setExpandedItem(expandedItem === item.id ? null : item.id)
                }
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-sky-500 to-indigo-600 flex items-center justify-center text-white">
                        <Database size={24} />
                      </div>
                      {/* Indicateur de conformité */}
                      <div
                        className={`absolute -top-1 -right-1 w-4 h-4 rounded-full ${
                          getItemConformityStatus(item).color
                        } border-2 border-white dark:border-gray-800`}
                        title={getItemConformityStatus(item).status}
                      />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900 dark:text-white">
                          {item.nom}
                        </h3>
                        <span
                          className="text-sm"
                          title={getItemConformityStatus(item).status}
                        >
                          {getItemConformityStatus(item).icon}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {item.type}
                        </span>
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            sensibiliteColors[item.sensibilite] ||
                            sensibiliteColors.Standard
                          }`}
                        >
                          {item.sensibilite || "Standard"}
                        </span>
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            getItemConformityStatus(item).status === "Conforme"
                              ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                              : getItemConformityStatus(item).status ===
                                "À réviser"
                              ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                              : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                          }`}
                        >
                          {getItemConformityStatus(item).status}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(item);
                      }}
                      className="p-2 text-sky-600 hover:bg-sky-100 dark:hover:bg-sky-900/30 rounded-lg"
                    >
                      <Edit2 size={18} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowDeleteConfirm(item.id);
                      }}
                      className="p-2 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg"
                    >
                      <Trash2 size={18} />
                    </button>
                    {expandedItem === item.id ? (
                      <ChevronUp size={20} className="text-gray-400" />
                    ) : (
                      <ChevronDown size={20} className="text-gray-400" />
                    )}
                  </div>
                </div>
              </div>

              {/* Détails étendus */}
              {expandedItem === item.id && (
                <div className="px-4 pb-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    {item.description && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                          Description
                        </p>
                        <p className="text-sm text-gray-900 dark:text-white">
                          {item.description}
                        </p>
                      </div>
                    )}
                    {item.localisation && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                          Localisation
                        </p>
                        <p className="text-sm text-gray-900 dark:text-white flex items-center gap-1">
                          <MapPin size={14} /> {item.localisation}
                        </p>
                      </div>
                    )}
                    {item.responsable && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                          Responsable
                        </p>
                        <p className="text-sm text-gray-900 dark:text-white flex items-center gap-1">
                          <User size={14} /> {item.responsable}
                        </p>
                      </div>
                    )}
                    {item.retention && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                          Rétention
                        </p>
                        <p className="text-sm text-gray-900 dark:text-white flex items-center gap-1">
                          <Clock size={14} /> {item.retention}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-4 mt-4">
                    <div className="flex items-center gap-2">
                      {item.consentement ? (
                        <CheckCircle size={16} className="text-green-500" />
                      ) : (
                        <XCircle size={16} className="text-gray-400" />
                      )}
                      <span className="text-xs text-gray-600 dark:text-gray-400">
                        Consentement
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {item.finalite_definie ? (
                        <CheckCircle size={16} className="text-green-500" />
                      ) : (
                        <XCircle size={16} className="text-gray-400" />
                      )}
                      <span className="text-xs text-gray-600 dark:text-gray-400">
                        Finalité définie
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {item.minimisation ? (
                        <CheckCircle size={16} className="text-green-500" />
                      ) : (
                        <XCircle size={16} className="text-gray-400" />
                      )}
                      <span className="text-xs text-gray-600 dark:text-gray-400">
                        Minimisation
                      </span>
                    </div>
                  </div>

                  {item.securite && item.securite.length > 0 && (
                    <div className="mt-4">
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                        Mesures de sécurité
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {item.securite.map((s) => (
                          <span
                            key={s}
                            className="px-2 py-1 bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-400 text-xs rounded"
                          >
                            {s}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Modal Ajouter/Modifier */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-sky-600 to-indigo-600 text-white p-6 rounded-t-xl flex items-center justify-between sticky top-0">
              <h2 className="text-xl font-bold">
                {editingItem ? "Modifier la donnée" : "Nouvelle donnée"}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-white hover:bg-white/20 p-2 rounded-lg"
              >
                <X size={24} />
              </button>
            </div>

            {/* Panneau de conformité Loi 25 */}
            <div
              className={`px-6 py-4 border-b dark:border-gray-700 ${conformity.statusBg}`}
            >
              {/* Statut principal */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{conformity.statusIcon}</span>
                  <div>
                    <span
                      className={`text-lg font-bold ${conformity.statusColor}`}
                    >
                      {conformity.status}
                    </span>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      {conformity.statusDescription}
                    </p>
                  </div>
                </div>
                <div className="text-right text-sm">
                  <div className="font-medium text-gray-700 dark:text-gray-300">
                    {conformity.legalPassed}/{conformity.legalTotal} obligations
                  </div>
                  <div className="text-xs text-gray-500">
                    {conformity.securityPassed}/{conformity.securityTotal}{" "}
                    sécurité
                  </div>
                </div>
              </div>

              {/* Barre de progression */}
              <div className="h-2 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden mb-3">
                <div
                  className={`h-full transition-all duration-300 ${
                    conformity.status === "Conforme"
                      ? "bg-green-500"
                      : conformity.status === "À réviser"
                      ? "bg-amber-500"
                      : "bg-red-500"
                  }`}
                  style={{ width: `${conformity.percentage}%` }}
                />
              </div>

              {/* Checklist Loi 25 */}
              {conformity.status !== "Conforme" && (
                <div className="text-xs space-y-1 max-h-24 overflow-y-auto">
                  {conformity.checks.legal
                    .filter((c) => !c.passed && c.required)
                    .map((c) => (
                      <div
                        key={c.key}
                        className="flex items-center gap-2 text-red-600 dark:text-red-400"
                      >
                        <span>✗</span>
                        <span>{c.label}</span>
                        <span className="text-gray-400">({c.article})</span>
                      </div>
                    ))}
                  {conformity.checks.security
                    .filter((c) => !c.passed && c.required)
                    .map((c) => (
                      <div
                        key={c.key}
                        className="flex items-center gap-2 text-red-600 dark:text-red-400"
                      >
                        <span>✗</span>
                        <span>{c.label}</span>
                        <span className="text-gray-400">({c.article})</span>
                      </div>
                    ))}
                  {!conformity.transferOk && (
                    <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                      <span>✗</span>
                      <span>Transfert hors Québec non documenté</span>
                      <span className="text-gray-400">(Art. 17)</span>
                    </div>
                  )}
                </div>
              )}
              {conformity.status === "Conforme" && (
                <p className="text-xs text-green-700 dark:text-green-400 flex items-center gap-2">
                  <CheckCircle size={14} />
                  Conforme aux exigences de la Loi 25 sur la protection des
                  renseignements personnels
                </p>
              )}
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Informations générales */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                  <FileText size={16} /> Informations générales
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Nom *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.nom}
                      onChange={(e) =>
                        setFormData({ ...formData, nom: e.target.value })
                      }
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-sky-500"
                      placeholder="Ex: Base de données clients"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Type de données
                    </label>
                    <select
                      value={formData.type}
                      onChange={(e) =>
                        setFormData({ ...formData, type: e.target.value })
                      }
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                    >
                      {types.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Sensibilité
                    </label>
                    <select
                      value={formData.sensibilite}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          sensibilite: e.target.value,
                        })
                      }
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                    >
                      {sensibilites.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          description: e.target.value,
                        })
                      }
                      rows={3}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                      placeholder="Description des données..."
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Données contenues
                    </label>
                    <input
                      type="text"
                      value={formData.donnees_contenues}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          donnees_contenues: e.target.value,
                        })
                      }
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                      placeholder="Ex: Nom, email, téléphone, adresse..."
                    />
                  </div>
                </div>
              </div>

              {/* Localisation et responsabilité */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                  <MapPin size={16} /> Localisation et responsabilité
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Localisation
                    </label>
                    <input
                      type="text"
                      value={formData.localisation}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          localisation: e.target.value,
                        })
                      }
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                      placeholder="Ex: Serveur Canada, Cloud Azure..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Responsable
                    </label>
                    <input
                      type="text"
                      value={formData.responsable}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          responsable: e.target.value,
                        })
                      }
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                      placeholder="Nom du responsable"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Durée de rétention
                    </label>
                    <input
                      type="text"
                      value={formData.retention}
                      onChange={(e) =>
                        setFormData({ ...formData, retention: e.target.value })
                      }
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                      placeholder="Ex: 5 ans, Durée du contrat + 2 ans..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Justification
                    </label>
                    <input
                      type="text"
                      value={formData.justification_retention}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          justification_retention: e.target.value,
                        })
                      }
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                      placeholder="Raison légale ou d'affaires"
                    />
                  </div>
                </div>
              </div>

              {/* Obligations Loi 25 */}
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-blue-800 dark:text-blue-300 mb-3 flex items-center gap-2">
                  <Shield size={16} /> Obligations légales (Loi 25)
                </h3>

                {/* Finalités */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Finalités de la collecte *{" "}
                    <span className="text-xs text-gray-500">(Art. 4, 5)</span>
                  </label>
                  <textarea
                    value={formData.finalites}
                    onChange={(e) =>
                      setFormData({ ...formData, finalites: e.target.value })
                    }
                    rows={2}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                    placeholder="Ex: Gestion de la relation client, facturation, communications marketing..."
                  />
                </div>

                {/* Base légale */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Base légale du traitement *{" "}
                    <span className="text-xs text-gray-500">(Art. 14)</span>
                  </label>
                  <select
                    value={formData.base_legale}
                    onChange={(e) =>
                      setFormData({ ...formData, base_legale: e.target.value })
                    }
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                  >
                    {basesLegales.map((b) => (
                      <option key={b} value={b}>
                        {b}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Checkboxes conformité */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <label className="flex items-center gap-3 p-3 bg-white dark:bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                    <input
                      type="checkbox"
                      checked={formData.consentement_explicite}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          consentement_explicite: e.target.checked,
                        })
                      }
                      className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Consentement explicite
                      </span>
                      <p className="text-xs text-gray-500">
                        Requis pour données sensibles
                      </p>
                    </div>
                  </label>
                  <label className="flex items-center gap-3 p-3 bg-white dark:bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                    <input
                      type="checkbox"
                      checked={formData.minimisation_validee}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          minimisation_validee: e.target.checked,
                        })
                      }
                      className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Minimisation validée
                      </span>
                      <p className="text-xs text-gray-500">
                        Seules données nécessaires
                      </p>
                    </div>
                  </label>
                  <label className="flex items-center gap-3 p-3 bg-white dark:bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                    <input
                      type="checkbox"
                      checked={formData.acces_limite}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          acces_limite: e.target.checked,
                        })
                      }
                      className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Accès limité
                      </span>
                      <p className="text-xs text-gray-500">
                        Personnes autorisées seulement
                      </p>
                    </div>
                  </label>
                </div>
              </div>

              {/* Transfert hors Québec */}
              <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-amber-800 dark:text-amber-300 mb-3 flex items-center gap-2">
                  <MapPin size={16} /> Transfert hors Québec{" "}
                  <span className="text-xs font-normal">(Art. 17)</span>
                </h3>

                <label className="flex items-center gap-3 mb-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.communication_hors_quebec}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        communication_hors_quebec: e.target.checked,
                      })
                    }
                    className="w-5 h-5 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Ces données sont communiquées à l'extérieur du Québec
                  </span>
                </label>

                {formData.communication_hors_quebec && (
                  <div className="ml-8 p-3 bg-white dark:bg-gray-800 rounded-lg border-l-4 border-amber-400">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Pays ou province destinataire *
                    </label>
                    <input
                      type="text"
                      value={formData.pays_destinataire}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          pays_destinataire: e.target.value,
                        })
                      }
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                      placeholder="Ex: Ontario, États-Unis, France..."
                    />
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                      ⚠️ Une évaluation des facteurs relatifs à la vie privée
                      peut être requise
                    </p>
                  </div>
                )}
              </div>

              {/* Sécurité */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                  <Lock size={16} /> Mesures de sécurité
                </h3>
                <div className="flex flex-wrap gap-2">
                  {securiteOptions.map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => toggleSecurite(option)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        (formData.securite || []).includes(option)
                          ? "bg-sky-600 text-white"
                          : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>

              {/* Boutons */}
              <div className="flex justify-end space-x-3 pt-4 border-t dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="px-6 py-3 bg-sky-600 text-white rounded-lg hover:bg-sky-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {isSaving && <Loader2 size={18} className="animate-spin" />}
                  {editingItem ? "Enregistrer" : "Ajouter"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Confirmation Suppression */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/30 mb-4">
                <Trash2 className="text-red-600 dark:text-red-400" size={24} />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                Supprimer cette donnée ?
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                Cette action est irréversible.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowDeleteConfirm(null)}
                  className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Annuler
                </button>
                <button
                  onClick={() => handleDelete(showDeleteConfirm)}
                  className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventaireManager;
